// SNIGJ - Admin SEO Configuration Page
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Globe, Search, Image, Share2, Save, RefreshCw, ExternalLink,
  Facebook, Twitter, MessageCircle, Eye, FileImage, Link2
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const AdminSeoPage = () => {
  const { language } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    site_title: '',
    site_description: '',
    og_title: '',
    og_description: '',
    og_image_url: '',
    og_site_name: '',
    og_locale: 'fr_CD',
    twitter_card: 'summary_large_image',
    favicon_url: '',
    keywords: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/seo-settings');
      if (response.data) {
        setSettings({
          site_title: response.data.site_title || '',
          site_description: response.data.site_description || '',
          og_title: response.data.og_title || '',
          og_description: response.data.og_description || '',
          og_image_url: response.data.og_image_url || '',
          og_site_name: response.data.og_site_name || '',
          og_locale: response.data.og_locale || 'fr_CD',
          twitter_card: response.data.twitter_card || 'summary_large_image',
          favicon_url: response.data.favicon_url || '',
          keywords: response.data.keywords || ''
        });
      }
    } catch (error) {
      console.error('Error fetching SEO settings:', error);
      toast.error(language === 'fr' ? 'Erreur lors du chargement des paramètres' : 'Error loading settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.post('/admin/seo-settings', settings);
      toast.success(language === 'fr' ? 'Paramètres SEO enregistrés' : 'SEO settings saved');
    } catch (error) {
      console.error('Error saving SEO settings:', error);
      toast.error(language === 'fr' ? 'Erreur lors de l\'enregistrement' : 'Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  // Preview Card Component
  const SocialPreview = ({ platform }) => {
    const ogImage = settings.og_image_url || '/og-image.png';
    const title = settings.og_title || settings.site_title || 'SNIGJ';
    const description = settings.og_description || settings.site_description || '';
    
    if (platform === 'facebook') {
      return (
        <div className="border rounded-lg overflow-hidden bg-white max-w-md">
          <div className="aspect-[1.91/1] bg-gray-100 relative">
            <img 
              src={ogImage} 
              alt="OG Preview" 
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = '/logo-justice.png';
              }}
            />
          </div>
          <div className="p-3 border-t">
            <p className="text-xs text-gray-500 uppercase tracking-wide">pulse.groupekazam.com</p>
            <h3 className="font-semibold text-gray-900 line-clamp-2 mt-1">{title}</h3>
            <p className="text-sm text-gray-600 line-clamp-2 mt-1">{description}</p>
          </div>
        </div>
      );
    }
    
    if (platform === 'twitter') {
      return (
        <div className="border rounded-2xl overflow-hidden bg-white max-w-md">
          <div className="aspect-[2/1] bg-gray-100 relative">
            <img 
              src={ogImage} 
              alt="Twitter Card Preview" 
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = '/logo-justice.png';
              }}
            />
          </div>
          <div className="p-3 border-t">
            <h3 className="font-semibold text-gray-900 line-clamp-1">{title}</h3>
            <p className="text-sm text-gray-600 line-clamp-2 mt-1">{description}</p>
            <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
              <Link2 className="w-3 h-3" />
              pulse.groupekazam.com
            </p>
          </div>
        </div>
      );
    }
    
    // WhatsApp
    return (
      <div className="bg-[#e5ddd5] p-3 rounded-lg max-w-sm">
        <div className="bg-white rounded-lg overflow-hidden shadow-sm">
          <div className="aspect-[1.91/1] bg-gray-100 relative">
            <img 
              src={ogImage} 
              alt="WhatsApp Preview" 
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = '/logo-justice.png';
              }}
            />
          </div>
          <div className="p-2">
            <h3 className="font-semibold text-sm text-gray-900 line-clamp-2">{title}</h3>
            <p className="text-xs text-gray-600 line-clamp-2 mt-1">{description}</p>
            <p className="text-xs text-[#25D366] mt-1">pulse.groupekazam.com</p>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-seo-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Globe className="w-6 h-6 text-yellow-500" />
              {language === 'fr' ? 'Configuration SEO' : 'SEO Configuration'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {language === 'fr' 
                ? 'Gérez les métadonnées et les aperçus des réseaux sociaux' 
                : 'Manage metadata and social media previews'}
            </p>
          </div>
          <Button onClick={handleSave} disabled={saving} data-testid="save-seo-btn">
            {saving ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {language === 'fr' ? 'Enregistrer' : 'Save'}
          </Button>
        </div>

        <Tabs defaultValue="general" className="space-y-6">
          <TabsList>
            <TabsTrigger value="general" className="gap-2">
              <Search className="w-4 h-4" />
              {language === 'fr' ? 'Général' : 'General'}
            </TabsTrigger>
            <TabsTrigger value="opengraph" className="gap-2">
              <Share2 className="w-4 h-4" />
              Open Graph
            </TabsTrigger>
            <TabsTrigger value="preview" className="gap-2">
              <Eye className="w-4 h-4" />
              {language === 'fr' ? 'Aperçu' : 'Preview'}
            </TabsTrigger>
          </TabsList>

          {/* General SEO Tab */}
          <TabsContent value="general" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="w-5 h-5" />
                  {language === 'fr' ? 'Métadonnées SEO' : 'SEO Metadata'}
                </CardTitle>
                <CardDescription>
                  {language === 'fr' 
                    ? 'Ces informations apparaissent dans les résultats de recherche Google'
                    : 'This information appears in Google search results'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="site_title">
                    {language === 'fr' ? 'Titre du site' : 'Site Title'}
                  </Label>
                  <Input
                    id="site_title"
                    value={settings.site_title}
                    onChange={(e) => handleChange('site_title', e.target.value)}
                    placeholder="SNIGJ - Système National Intégré de Gestion de la Justice"
                    data-testid="input-site-title"
                  />
                  <p className="text-xs text-muted-foreground">
                    {settings.site_title.length}/60 {language === 'fr' ? 'caractères' : 'characters'}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="site_description">
                    {language === 'fr' ? 'Description du site' : 'Site Description'}
                  </Label>
                  <Textarea
                    id="site_description"
                    value={settings.site_description}
                    onChange={(e) => handleChange('site_description', e.target.value)}
                    placeholder={language === 'fr' 
                      ? 'Plateforme intégrée de gestion du système judiciaire...'
                      : 'Integrated platform for justice system management...'}
                    rows={3}
                    data-testid="input-site-description"
                  />
                  <p className="text-xs text-muted-foreground">
                    {settings.site_description.length}/160 {language === 'fr' ? 'caractères recommandés' : 'characters recommended'}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="keywords">
                    {language === 'fr' ? 'Mots-clés' : 'Keywords'}
                  </Label>
                  <Input
                    id="keywords"
                    value={settings.keywords}
                    onChange={(e) => handleChange('keywords', e.target.value)}
                    placeholder="SNIGJ, Justice, RDC, Congo, Tribunal"
                    data-testid="input-keywords"
                  />
                  <p className="text-xs text-muted-foreground">
                    {language === 'fr' ? 'Séparez les mots-clés par des virgules' : 'Separate keywords with commas'}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileImage className="w-5 h-5" />
                  Favicon
                </CardTitle>
                <CardDescription>
                  {language === 'fr' 
                    ? 'L\'icône qui apparaît dans l\'onglet du navigateur'
                    : 'The icon that appears in the browser tab'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 border rounded-lg flex items-center justify-center bg-gray-50">
                    <img 
                      src={settings.favicon_url || '/favicon.ico'} 
                      alt="Favicon" 
                      className="w-8 h-8"
                      onError={(e) => {
                        e.target.src = '/logo-justice.png';
                      }}
                    />
                  </div>
                  <div className="flex-1 space-y-2">
                    <Label htmlFor="favicon_url">
                      {language === 'fr' ? 'URL du favicon (optionnel)' : 'Favicon URL (optional)'}
                    </Label>
                    <Input
                      id="favicon_url"
                      value={settings.favicon_url}
                      onChange={(e) => handleChange('favicon_url', e.target.value)}
                      placeholder="https://example.com/favicon.ico"
                      data-testid="input-favicon-url"
                    />
                    <p className="text-xs text-muted-foreground">
                      {language === 'fr' 
                        ? 'Laissez vide pour utiliser le favicon par défaut'
                        : 'Leave empty to use default favicon'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Open Graph Tab */}
          <TabsContent value="opengraph" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Share2 className="w-5 h-5" />
                  Open Graph / {language === 'fr' ? 'Réseaux sociaux' : 'Social Media'}
                </CardTitle>
                <CardDescription>
                  {language === 'fr' 
                    ? 'Contrôlez l\'apparence des liens partagés sur Facebook, WhatsApp, Twitter, etc.'
                    : 'Control how shared links appear on Facebook, WhatsApp, Twitter, etc.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="og_title">
                    {language === 'fr' ? 'Titre OG' : 'OG Title'}
                  </Label>
                  <Input
                    id="og_title"
                    value={settings.og_title}
                    onChange={(e) => handleChange('og_title', e.target.value)}
                    placeholder="SNIGJ - Système National de Gestion de la Justice"
                    data-testid="input-og-title"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="og_description">
                    {language === 'fr' ? 'Description OG' : 'OG Description'}
                  </Label>
                  <Textarea
                    id="og_description"
                    value={settings.og_description}
                    onChange={(e) => handleChange('og_description', e.target.value)}
                    placeholder={language === 'fr' 
                      ? 'Description qui apparaîtra dans les aperçus des réseaux sociaux...'
                      : 'Description that will appear in social media previews...'}
                    rows={3}
                    data-testid="input-og-description"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="og_image_url">
                    {language === 'fr' ? 'URL de l\'image OG' : 'OG Image URL'}
                  </Label>
                  <Input
                    id="og_image_url"
                    value={settings.og_image_url}
                    onChange={(e) => handleChange('og_image_url', e.target.value)}
                    placeholder="https://pulse.groupekazam.com/og-image.png"
                    data-testid="input-og-image"
                  />
                  <p className="text-xs text-muted-foreground">
                    {language === 'fr' 
                      ? 'Recommandé: 1200x630 pixels. Laissez vide pour utiliser l\'image par défaut.'
                      : 'Recommended: 1200x630 pixels. Leave empty to use default image.'}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="og_site_name">
                      {language === 'fr' ? 'Nom du site' : 'Site Name'}
                    </Label>
                    <Input
                      id="og_site_name"
                      value={settings.og_site_name}
                      onChange={(e) => handleChange('og_site_name', e.target.value)}
                      placeholder="SNIGJ - Ministère de la Justice RDC"
                      data-testid="input-og-site-name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="og_locale">Locale</Label>
                    <Select 
                      value={settings.og_locale} 
                      onValueChange={(value) => handleChange('og_locale', value)}
                    >
                      <SelectTrigger data-testid="select-og-locale">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fr_CD">Français (RDC)</SelectItem>
                        <SelectItem value="fr_FR">Français (France)</SelectItem>
                        <SelectItem value="en_US">English (US)</SelectItem>
                        <SelectItem value="en_GB">English (UK)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="twitter_card">Twitter Card Type</Label>
                  <Select 
                    value={settings.twitter_card} 
                    onValueChange={(value) => handleChange('twitter_card', value)}
                  >
                    <SelectTrigger data-testid="select-twitter-card">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="summary">Summary</SelectItem>
                      <SelectItem value="summary_large_image">Summary Large Image</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Preview Tab */}
          <TabsContent value="preview" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  {language === 'fr' ? 'Aperçu des partages' : 'Share Previews'}
                </CardTitle>
                <CardDescription>
                  {language === 'fr' 
                    ? 'Voici comment vos liens apparaîtront sur les réseaux sociaux'
                    : 'This is how your links will appear on social media'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Facebook Preview */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <Facebook className="w-4 h-4 text-blue-600" />
                      Facebook
                    </div>
                    <SocialPreview platform="facebook" />
                  </div>

                  {/* Twitter Preview */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <Twitter className="w-4 h-4 text-sky-500" />
                      Twitter / X
                    </div>
                    <SocialPreview platform="twitter" />
                  </div>

                  {/* WhatsApp Preview */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <MessageCircle className="w-4 h-4 text-green-500" />
                      WhatsApp
                    </div>
                    <SocialPreview platform="whatsapp" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Testing Tools */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ExternalLink className="w-5 h-5" />
                  {language === 'fr' ? 'Outils de validation' : 'Validation Tools'}
                </CardTitle>
                <CardDescription>
                  {language === 'fr' 
                    ? 'Utilisez ces outils pour vérifier vos paramètres Open Graph'
                    : 'Use these tools to verify your Open Graph settings'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  <a
                    href="https://developers.facebook.com/tools/debug/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Facebook className="w-4 h-4" />
                    Facebook Debugger
                  </a>
                  <a
                    href="https://cards-dev.twitter.com/validator"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors"
                  >
                    <Twitter className="w-4 h-4" />
                    Twitter Card Validator
                  </a>
                  <a
                    href="https://www.linkedin.com/post-inspector/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    LinkedIn Inspector
                  </a>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default AdminSeoPage;
